import React, { useState, useRef, useEffect, DragEvent } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api'; // 帶登入 token（後端只讓登入者動自己店家的圖片）

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];

interface ImageUploaderProps {
  currentImageUrl?: string;
  onImageUploaded: (imageUrl: string) => void;
  onImageRemoved?: () => void;
  folder: 'stores' | 'services' | 'staff';
  label?: string;
  helpText?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  currentImageUrl,
  onImageUploaded,
  onImageRemoved,
  folder,
  label = '上傳圖片',
  helpText = '拖放圖片到此處，或點擊選擇檔案',
}) => {
  const { t } = useTranslation('translation');
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 同步 currentImageUrl 變更到 previewUrl（用於載入已存在的圖片）
  useEffect(() => {
    setPreviewUrl(currentImageUrl || null);
  }, [currentImageUrl]);

  const handleFileSelect = async (file: File) => {
    // 驗證檔案類型（和後端、Supabase booking-images 儲存空間同一份清單）
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error(t('imageUploader.invalidFileType'));
      return;
    }

    // 驗證檔案大小（10MB 限制）
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('imageUploader.fileSizeError'));
      return;
    }

    // 生成預覽
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);

    // 上傳檔案
    await uploadImage(file);
  };

  const deleteOldImage = async (imageUrl: string) => {
    try {
      await api.delete('/upload/image', { data: { imageUrl } });
      console.log('舊圖片已刪除:', imageUrl);
    } catch (error: any) {
      console.error('刪除舊圖片失敗:', error);
      // 不拋出錯誤，繼續上傳新圖片
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);

    try {
      // 如果有舊圖片，先刪除舊圖片（非 blob URL）
      if (currentImageUrl && !currentImageUrl.startsWith('blob:')) {
        await deleteOldImage(currentImageUrl);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { url } = response.data;
      console.log('圖片上傳成功，URL:', url);

      // 清除舊的 blob URL
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(url);
      onImageUploaded(url);
      toast.success(t('common.messages.createSuccess'));
    } catch (error: any) {
      console.error('圖片上傳失敗:', error);
      toast.error(error.response?.data?.error || t('common.messages.createFailed'));

      // 清除 blob URL
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(currentImageUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveImage = async () => {
    // 如果有真實的圖片 URL（非 blob），先刪除後端的圖片
    if (currentImageUrl && !currentImageUrl.startsWith('blob:')) {
      try {
        await api.delete('/upload/image', { data: { imageUrl: currentImageUrl } });
        toast.success(t('common.messages.deleteSuccess'));
      } catch (error: any) {
        console.error('刪除圖片失敗:', error);
        toast.error(t('common.messages.deleteFailed'));
        return; // 如果刪除失敗，不清除前端預覽
      }
    }

    // 清除前端預覽
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // 通知父組件
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {/* 拖放區域或圖片預覽 */}
      {previewUrl ? (
        <div className="relative group">
          {/* 圖片預覽 */}
          <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                console.error('圖片載入失敗:', previewUrl);
                e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="gray">圖片載入失敗</text></svg>';
              }}
              onLoad={() => {
                console.log('圖片載入成功:', previewUrl);
              }}
            />

            {/* 上傳中遮罩 */}
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}

            {/* 移除按鈕 */}
            {!uploading && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 更換圖片按鈕 */}
          {!uploading && (
            <button
              type="button"
              onClick={handleClickUpload}
              className="mt-2 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium"
            >
              {t('imageUploader.changeImage')}
            </button>
          )}
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
              : 'border-gray-300 dark:border-slate-600 hover:border-brand-400 dark:hover:border-brand-500'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          {uploading ? (
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('imageUploader.uploading')}</p>
            </div>
          ) : (
            <>
              <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{helpText}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                {t('imageUploader.fileRequirements')}
              </p>
            </>
          )}
        </div>
      )}

      {/* 隱藏的檔案輸入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );
};

export default ImageUploader;
